import { clsx, useDebouncedElementSize, type WithClassName } from "@regrello/core-utils";
import type React from "react";
import { useLayoutEffect, useRef, useState } from "react";

import { RegrelloTooltip, type RegrelloTooltipProps } from "../../overlay/tooltip/RegrelloTooltip";

export interface RegrelloTooltippedTextProps extends WithClassName {
  children: React.ReactNode;

  /**
   * Number of lines to clamp the text to before applying the tooltip.
   *
   * @default null // Uses "truncate" to ensure that ellipsis are shown.
   */
  lines?: number;

  /** Props to pass to the tooltip. Pass `disabled={true}` to disable the tooltip altogether. */
  tooltipProps?: Pick<
    RegrelloTooltipProps,
    "align" | "contentProps" | "disabled" | "enableHoverableContent" | "maxWidth" | "side" | "variant"
  >;
}

/**
 * Component that shows overflowing content in a tooltip if and only if the child content overflows
 * its parent.
 */
export function RegrelloTooltippedText({ children, className, lines, tooltipProps }: RegrelloTooltippedTextProps) {
  const textElementRef = useRef<HTMLDivElement | null>(null);

  const [tooltipContent, setTooltipContent] = useState<string | null>(null);

  const textElementWidth = useDebouncedElementSize({ elementRef: textElementRef });

  useLayoutEffect(() => {
    if (textElementWidth) {
      // This hook needs to run whenever text width change
    }

    if (textElementRef.current == null) {
      return;
    }
    const contentWidth = textElementRef.current.scrollWidth;
    const availableWidth = textElementRef.current.clientWidth;
    const contentHeight = textElementRef.current.scrollHeight;
    const availableHeight = textElementRef.current.clientHeight;

    if (contentWidth > availableWidth || contentHeight > availableHeight) {
      setTooltipContent(textElementRef.current.textContent);
    } else {
      setTooltipContent(null);
    }
  }, [textElementWidth]);

  return (
    <RegrelloTooltip align="center" content={tooltipContent} {...tooltipProps}>
      {/* (clewis): This text element must be display:block in order for text ellipses to show. */}
      <div
        ref={textElementRef}
        className={clsx(
          {
            truncate: lines == null || lines === 1, // Ellipses are not always shown with line-clamp-1; this is more reliable.
            "line-clamp-2": lines === 2,
            "line-clamp-3": lines === 3,
            "line-clamp-4": lines === 4,
            "line-clamp-5": lines != null && lines >= 5,
          },
          className,
        )}
      >
        {children}
      </div>
    </RegrelloTooltip>
  );
}
